import { useState, useRef, useEffect } from 'react';
import { versionLine, versionTitle } from '@/lib/version';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../lib/config';
import { useAuthStore } from '../stores/authStore';

type AuthStep = 'initial' | 'sending' | 'code' | 'verifying';

export function SignInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();
  const token = useAuthStore((s) => s.token);
  const lastEmail = useAuthStore((s) => s.lastEmail);
  const setLastEmail = useAuthStore((s) => s.setLastEmail);
  const prefilledEmail = searchParams.get('email') || '';
  const [email, setEmail] = useState(prefilledEmail || lastEmail || '');
  const [authStep, setAuthStep] = useState<AuthStep>(prefilledEmail ? 'sending' : 'initial');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const emailAutoFired = useRef(false);

  // Auto-fire OTP when redirected from homepage with ?email=
  useEffect(() => {
    if (token || emailAutoFired.current || !prefilledEmail) return;
    emailAutoFired.current = true;
    handleEmailSignIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preserve ?q= from answer pages so it survives the auth flow
  const pendingQuery = searchParams.get('q');
  if (pendingQuery) {
    sessionStorage.setItem('zac_pending_query', pendingQuery);
  }

  // Already logged in — skip sign-in page entirely
  if (token) {
    const savedQuery = sessionStorage.getItem('zac_pending_query');
    if (savedQuery) {
      sessionStorage.removeItem('zac_pending_query');
      return <Navigate to={`/chat?q=${encodeURIComponent(savedQuery)}`} replace />;
    }
    return <Navigate to="/settings" replace />;
  }

  const handleGoogleSignIn = async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/google/url`);
      if (!res.ok) {
        setError('Failed to connect to Google. Please try again.');
        return;
      }
      const data = await res.json();
      if (typeof data.url !== 'string' || !data.url.startsWith('https://')) {
        setError('Invalid response from server. Please try again.');
        return;
      }
      window.location.href = data.url;
    } catch {
      setError('Failed to connect to Google. Please try again.');
    }
  };

  const handleEmailSignIn = async () => {
    if (!email.trim()) return;
    setError(null);
    setAuthStep('sending');

    try {
      const res = await fetch(`${API_BASE}/auth/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || 'Failed to send verification code.');
        setAuthStep('initial');
        return;
      }

      setAuthStep('code');
    } catch {
      setError('Network error. Please try again.');
      setAuthStep('initial');
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) return;
    setError(null);
    setAuthStep('verifying');

    try {
      const res = await fetch(`${API_BASE}/auth/otp/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code: verificationCode.trim() }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || 'Invalid or expired code.');
        setAuthStep('code');
        return;
      }

      const data = await res.json();
      setAuth({
        token: data.token,
        user: data.user,
      });
      setLastEmail(email.trim());
      const savedQuery = sessionStorage.getItem('zac_pending_query');
      if (savedQuery) {
        sessionStorage.removeItem('zac_pending_query');
        navigate(`/chat?q=${encodeURIComponent(savedQuery)}`, { replace: true });
      } else {
        navigate('/settings', { replace: true });
      }
    } catch {
      setError('Network error. Please try again.');
      setAuthStep('code');
    }
  };

  const handleResendEmail = async () => {
    setError(null);
    try {
      await fetch(`${API_BASE}/auth/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
    } catch {
      // silently fail
    }
  };

  return (
    <main className="flex items-center justify-center min-h-[calc(100vh-88px)] px-8">
        <div className="w-full max-w-md mx-auto">
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            {authStep === 'sending' && prefilledEmail && (
              <div className="flex items-center justify-center py-8">
                <div className="h-6 w-6 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
              </div>
            )}

            {(authStep === 'initial' || (authStep === 'sending' && !prefilledEmail)) && (
              <>
                <div className="space-y-4">
                  <button
                    onClick={handleGoogleSignIn}
                    className="w-full bg-white hover:bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-3 border border-gray-300"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </button>

                  <div className="flex items-center gap-4">
                    <div className="flex-1 h-px bg-border"></div>
                    <span className="text-muted-foreground text-sm">OR</span>
                    <div className="flex-1 h-px bg-border"></div>
                  </div>

                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleEmailSignIn()}
                    placeholder="Enter your email"
                    className="w-full bg-input border border-border rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />

                  <button
                    onClick={handleEmailSignIn}
                    disabled={authStep === 'sending'}
                    className="w-full bg-[#2D8CFF] hover:bg-[#2681F0] text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    {authStep === 'sending' ? 'Sending...' : 'Continue with email'}
                  </button>
                </div>

                <p className="text-muted-foreground text-xs mt-6 text-center">
                  By continuing, you acknowledge our{' '}
                  <a href="#" className="text-primary hover:underline">Privacy Policy</a>
                  {' '}and{' '}
                  <a href="#" className="text-primary hover:underline">Terms of Service</a>.
                </p>
                <p className="text-muted-foreground/60 text-[11px] mt-3 text-center tabular-nums" title={versionTitle()}>
                  {versionLine()}
                </p>
              </>
            )}

            {(authStep === 'code' || authStep === 'verifying') && (
              <div className="bg-card border border-border rounded-xl p-8 text-center">
                <p className="text-muted-foreground text-sm mb-1">Enter the verification code sent to</p>
                <p className="font-medium mb-6">{email}</p>

                <div className="space-y-4">
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleVerifyCode()}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="w-full bg-input border border-border rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-center tracking-widest text-lg"
                  />

                  <button
                    onClick={handleVerifyCode}
                    disabled={authStep === 'verifying'}
                    className="w-full bg-[#2D8CFF] hover:bg-[#2681F0] text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    {authStep === 'verifying' ? 'Verifying...' : 'Verify Code'}
                  </button>
                </div>

                <p className="text-muted-foreground text-sm mt-6">
                  Not seeing the email in your inbox?{' '}
                  <button
                    onClick={handleResendEmail}
                    className="text-primary hover:underline"
                  >
                    Try sending again
                  </button>
                </p>
              </div>
            )}
          </div>
    </main>
  );
}
